/**
 * @desc: 筛选器视图层（分组布局 + 展开收起）
 */
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
// import classnames from 'classnames';
import { useLatest } from 'ahooks';
import FilterGroup from '../FilterGroup';
import StickyLayer from './components/StickyLayer';
import FilterBody from './components/FilterBody';
import FilterItemRenderer from '../FilterItemRenderer';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { useFilterOptionList } from '@intelligence/hooks/useFilterOptionList';
import useFilterSticky from './_hooks/useFilterSticky';
import { useFilterGroupDiagnostics } from './_hooks/useFilterGroupDiagnostics';
import { useOverflowDetection } from './_hooks/useOverflowDetection';
import { useStickyPortal } from './_hooks/useStickyPortal';
import { buildStickySummaryItems, getGroupedFieldSet, getItemFields, renderFieldNodes } from './utils';

import type { IFilterGroup, IFilterItemConfig, IFilterValues } from '@intelligence/v3/filterCore/types';

interface IFilterViewProps extends Omit<IBaseProps, 'onChange'> {
  /** 当前语言环境 */
  locale: string;
  /** loading 状态：items 区显示骨架屏，操作按钮禁用 */
  loading?: boolean;
  /** 筛选器项前置内容 */
  beforeItemsContent?: React.ReactNode;
  /** 吸顶态默认头部区域前置内容 */
  stickyBeforeSummaryContent?: React.ReactNode;
  /** 是否显示操作栏 */
  useActionBar?: boolean;
  /** 是否启用更多筛选 */
  useMoreFilter?: boolean;
  /** 是否启用自定义布局 */
  useCustomLayout?: boolean;
  /** 是否启用吸顶 */
  useSticky?: boolean;
  /** 吸顶标题 */
  stickyHeaderTitle?: string;
  /** 吸顶顶部偏移 */
  stickyTopOffset?: number;
  /** 吸顶面板挂载容器，默认 document.body */
  stickyPortalContainer?: HTMLElement | string | (() => HTMLElement | null) | null;
  /** 筛选器分组配置 */
  filterGroups?: IFilterGroup[];
  /** 是否显示我的筛选器标识 */
  showSavedFilterIndicator?: boolean;
  /** Filter 内部注入的额外操作内容 */
  innerExtraActions?: React.ReactNode;
  /** 重置 tooltip */
  resetTooltip?: string;
  /** 暴露给外部页面的额外操作内容 */
  extraActions?: React.ReactNode;
  /** 筛选器值是否未被修改 */
  pristine: boolean;
  /** 筛选器项配置列表 */
  items: IFilterItemConfig[];
  /** 当前值（draft，受控） */
  value: IFilterValues;
  /** 值变更回调 */
  onChange: (field: string, value: any) => void;
  /** 确认回调 */
  onConfirm: () => void;
  /** 重置回调 */
  onReset: () => void;
  /** 展开/收起回调（通知外部） */
  onToggleExpand?: (expanded: boolean) => void;
}

const renderSkeletonNodes = (count: number): React.ReactNode[] => {
  return Array.from({ length: count }).map((_, index) => {
    return <div key={`skeleton-${index}`} className="dbi-component-filter-skeleton__item" />;
  });
};

const FilterView: React.FC<IFilterViewProps> = (props) => {
  const {
    style,
    className,
    locale,
    loading = false,
    beforeItemsContent,
    stickyBeforeSummaryContent,
    useActionBar = true,
    useMoreFilter = true,
    useCustomLayout = false,
    useSticky = false,
    stickyHeaderTitle,
    stickyTopOffset = 0,
    stickyPortalContainer,
    filterGroups = [],
    showSavedFilterIndicator = false,
    innerExtraActions,
    resetTooltip,
    extraActions,
    pristine,
    items,
    value,
    onChange,
    onConfirm,
    onReset,
    onToggleExpand,
  } = props;

  const { formatMessage } = useIntlExtend();
  const onChangeRef = useLatest(onChange);

  const containerRef = useRef<HTMLDivElement>(null);
  const filterBodyRef = useRef<HTMLDivElement>(null);
  const stickyAnchorRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [stickyExpanded, setStickyExpanded] = useState(false);

  const { dataSourceFilterOptionsAll, googlePlatformFilterOptions } = useFilterOptionList({ formatMessage });
  const previewCtx = useMemo(() => {
    return {
      dataSourceOptions: dataSourceFilterOptionsAll,
      platformOptions: googlePlatformFilterOptions,
    };
  }, [dataSourceFilterOptionsAll, googlePlatformFilterOptions]);

  const sticky = useFilterSticky({
    enabled: useSticky,
    triggerElementRef: stickyAnchorRef,
    heightElementRef: filterBodyRef,
    topOffset: stickyTopOffset,
  });

  const handleFilterItemChange = (field: string, fieldValue: any): void => {
    onChangeRef.current?.(field, fieldValue);
  };

  const visibleItems = useMemo(() => {
    return items.filter((item) => item.visible !== false);
  }, [items]);

  useFilterGroupDiagnostics({ items, visibleItems, filterGroups });

  const renderedItemMap = useMemo(() => {
    return visibleItems.reduce<Map<string, React.ReactNode>>((map, item) => {
      map.set(
        item.field,
        <FilterItemRenderer
          key={item.field}
          locale={locale}
          item={item}
          value={value[item.field]}
          onChange={handleFilterItemChange}
        />,
      );
      return map;
    }, new Map<string, React.ReactNode>());
  }, [locale, visibleItems, value]);

  const renderedGroups = useMemo(() => {
    let headFields: string[] = getItemFields(visibleItems);

    if (filterGroups.length > 0) {
      const groupedFieldSet = getGroupedFieldSet(filterGroups);
      headFields = headFields.filter((field) => {
        return !groupedFieldSet.has(field);
      });
    }

    const nodes: React.ReactNode[] = [];
    const skeletonCount = visibleItems.length || 6;
    let headContent: React.ReactNode = renderFieldNodes(headFields, renderedItemMap);

    if (loading) {
      headContent = renderSkeletonNodes(skeletonCount);
    }

    nodes.push(
      <FilterGroup key="group_head" groupKey="group_head">
        {beforeItemsContent}
        {headContent}
      </FilterGroup>,
    );

    if (loading) {
      return nodes;
    }

    filterGroups.forEach((group) => {
      nodes.push(
        <FilterGroup
          key={group.key}
          groupKey={group.key}
          title={group.title}
          icon={group.icon}
          style={group.style}
          className={group.className}
        >
          {renderFieldNodes(group.filterKeys, renderedItemMap)}
        </FilterGroup>,
      );
    });

    return nodes;
  }, [beforeItemsContent, filterGroups, loading, renderedItemMap, visibleItems]);

  const stickySummaryItems = useMemo(() => {
    return buildStickySummaryItems({
      useSticky,
      items: visibleItems,
      values: value,
      previewCtx,
      formatMessage,
    });
  }, [useSticky, visibleItems, value, previewCtx, formatMessage]);

  const hasOverflow = useOverflowDetection({
    containerRef,
    enabled: useMoreFilter,
    useSticky,
    isStickyFixed: sticky.filterFixed,
    isStickyExpanded: stickyExpanded,
  });

  useEffect(() => {
    if (!useSticky || !sticky.filterFixed) {
      setStickyExpanded(false);
    }
  }, [useSticky, sticky.filterFixed]);

  const showAll = expanded || stickyExpanded || !useActionBar || !useMoreFilter;

  const stickyPortalTarget = useStickyPortal({
    enabled: useSticky,
    stickyPortalContainer,
  });

  const handleToggleExpand = (): void => {
    setExpanded((prevExpanded) => {
      const nextExpanded = !prevExpanded;
      onToggleExpand?.(nextExpanded);
      return nextExpanded;
    });
  };

  const filterBody = (
    <FilterBody
      style={style}
      className={className}
      filterBodyRef={filterBodyRef}
      containerRef={containerRef}
      showAll={showAll}
      useCustomLayout={useCustomLayout}
      renderedGroups={renderedGroups}
      useActionBar={useActionBar}
      loading={loading}
      pristine={pristine}
      hasOverflow={hasOverflow}
      expanded={expanded}
      showStickyClose={useSticky && sticky.filterFixed && stickyExpanded}
      innerExtraActions={innerExtraActions}
      resetTooltip={resetTooltip}
      extraActions={extraActions}
      onConfirm={onConfirm}
      onReset={onReset}
      onStickyClose={() => {
        setStickyExpanded(false);
      }}
      onToggleExpand={handleToggleExpand}
    />
  );

  if (!useSticky) {
    return filterBody;
  }

  const stickyLayer = (
    <StickyLayer
      isFilterFixed={sticky.filterFixed}
      isExpanded={stickyExpanded}
      topOffset={stickyTopOffset}
      headerTitle={formatMessage({ id: stickyHeaderTitle })}
      showSavedFilterIndicator={showSavedFilterIndicator}
      beforeSummaryContent={stickyBeforeSummaryContent}
      summaryItems={stickySummaryItems}
      stickyPanel={filterBody}
      onHeaderClick={() => {
        setStickyExpanded(true);
      }}
      onMaskClick={() => {
        setStickyExpanded(false);
      }}
    />
  );

  return (
    <div
      style={{ minHeight: sticky.filterFixed ? sticky.filterHeight : undefined }}
      className="dbi-common-filter-v3__sticky-box"
    >
      <div ref={stickyAnchorRef} className="dbi-common-filter-v3__sticky-anchor">
        {!sticky.filterFixed && stickyLayer}
      </div>
      {sticky.filterFixed && stickyPortalTarget ? createPortal(stickyLayer, stickyPortalTarget) : null}
    </div>
  );
};

export default FilterView;
